import {
  Bot,
  Calendar,
  ChartLine,
  FileBarChart,
  ListTodo,
  MessageSquare,
  PhoneCall,
  PhoneIncoming,
  Sparkles,
  Webhook,
  type LucideIcon,
} from 'lucide-react';

/**
 * Visual + descriptive metadata for known legacy module IDs. Keys are the
 * stringified module IDs returned by /api/obd/module. Unknown modules
 * fall back to a generic icon + the API-provided name.
 *
 * The legacy backend uses these IDs:
 *   3  = Webhook
 *   5  = Dedicated CLI
 *   6  = Inbound (requires 5)
 *   7  = TTS
 *   9  = SMS
 *   10 = Continuous Voice Campaign (TTS-variant)
 *   11 = Report Scheduling
 *   12 = Report Scheduling (variant)
 */
export const MODULE_META: Record<
  string,
  { icon: LucideIcon; description?: string }
> = {
  '3': {
    icon: Webhook,
    description:
      'POST call event payloads to your endpoints. Requires API access key + secret.',
  },
  '5': {
    icon: PhoneCall,
    description: 'Dedicated outbound CLIs allocated only to this user.',
  },
  '6': {
    icon: PhoneIncoming,
    description: 'Inbound call campaigns with agent routing. Requires Dedicated CLI.',
  },
  '7': {
    icon: Bot,
    description: 'Generate audio prompts dynamically with text-to-speech.',
  },
  '9': {
    icon: MessageSquare,
    description: 'SMS campaigns with delivery tracking and webhooks.',
  },
  '10': {
    icon: Sparkles,
    description: 'Continuous voice campaigns with TTS-driven prompts.',
  },
  '11': {
    icon: Calendar,
    description: 'Schedule recurring report downloads.',
  },
  '12': {
    icon: ChartLine,
    description: 'Schedule recurring summary reports.',
  },
  '14': {
    icon: FileBarChart,
    description: 'Select multiple campaigns and generate reports in bulk.',
  },
};

export function getModuleMeta(id: string | number) {
  const key = String(id);
  return MODULE_META[key] ?? { icon: ListTodo };
}

/**
 * IDs that enable the TTS plan + language picker inside the module card.
 * Only the dedicated TTS module (7) needs these — Continuous Voice
 * Campaigns (10) ship without TTS plan / language assignment per
 * product requirements.
 */
export const TTS_MODULE_IDS = new Set(['7']);
/** The webhook module that requires accessKey + secretKey. */
export const WEBHOOK_MODULE_ID = '3';
/** Inbound module — requires Dedicated CLI (5) to also be selected. */
export const INBOUND_MODULE_ID = '6';
/** Dedicated CLI prerequisite for Inbound. */
export const DEDICATED_CLI_MODULE_ID = '5';
/** Bulk Report — visible only for "User" role, not resellers. */
export const BULK_REPORT_MODULE_ID = '14';
/** Voice Plan Logs — visible only for "Reseller" role, never for end users. */
export const VOICE_PLAN_LOGS_MODULE_ID = '15';
